import type { MouseEvent, KeyboardEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SourceTypes } from "@common/types/server-list-types";
import {
  GatewayMachine,
  GatewayWithMachinesViewStateTypes,
} from "@src/types/gateways-with-machines";
import {
  updateConnectionView,
  updateMaximumNumberOfMonitoredItems,
  updateNodeManagerActiveView,
  type NodeManagerActiveViewType,
} from "@src/reducers/your-reducer";
import { RootState } from "@src/reducers/store";
import { useI18N } from "@src/language/language-context";
import { DeviceAuthOption } from "@src/types/device-auth-option";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";
import { getMachineActionDisabledReason } from "@src/cloud-connect/gateways-with-machines/entities/machine-action-state";

interface UseGatewayMachineListRowHookInput {
  gatewayData: GatewayWithMachinesViewStateTypes;
  machine: GatewayMachine;
}

export const useGatewayMachineListRowHook = ({
  gatewayData,
  machine,
}: UseGatewayMachineListRowHookInput) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );
  const showOTA = useFeatureFlag("show-ota", false);
  const showShotDataTable = useFeatureFlag("shotdata-table", false);

  const texts = {
    machineIconAltText: i18n(
      "modules.gatewayMachineListRow.machineIconAltText",
    ),
    gatewayIconAltText: i18n(
      "modules.gatewayMachineListRow.gatewayIconAltText",
    ),
    goToTreeStructure: i18n("modules.gatewayMachineListRow.goToTreeStructure"),
    goToConditionMonitoring: i18n(
      "modules.gatewayMachineListRow.goToConditionMonitoring",
    ),
    goToDashboard: i18n("modules.gatewayMachineListRow.goToDashboard"),
    noSoftwareVersion: i18n("modules.gatewayMachineListRow.noSoftwareVersion"),
    noMachineId: i18n("modules.gatewayMachineListRow.noMachineId"),
    copyTooltip: i18n("modules.machinesListRow.copyTooltip"),
    successMessage: i18n("modules.machinesListRow.successMessage"),
    errorMessage: i18n("modules.machinesListRow.errorMessage"),
    goToMachineSettings: i18n(
      "modules.gatewayMachineListRow.goToMachineSettings",
    ),
    goToRequestTechnicalSupport: i18n(
      "modules.ticketSupportForMachineRowIconButton.goToRequestTechnicalSupport",
    ),
    machineConfigurationWarning: i18n(
      "modules.machinesListRow.machineConfigurationWarning",
    ),
    missingMachineSkillTooltip: i18n(
      "modules.machinesListRow.missingMachineSkillTooltip",
    ),
    goToShotDataTable: i18n("modules.gatewayMachineListRow.goToShotDataTable"),
  };

  const goToNodeManager = () =>
    navigateTo(`${Route.NODE_MANAGER_PAGE}?origin=gateways`);

  const isMachineProxy = machine.source_type === "MACHINE_V2_PROXY";
  const hasOpcUaSkill = isMachineProxy
    ? Boolean(machine.has_opc_ua_skill)
    : true;
  const actionDisabledReason = getMachineActionDisabledReason(machine);
  const opcUaDataSourceId = machine.opc_ua_data_source_id || "";
  const sourceForOpcUaActions: SourceTypes = {
    ...machine,
    source_id: opcUaDataSourceId,
  } as SourceTypes;
  const showActionLinks =
    machine.connection_state !== "DISCONNECTED" || isMachineProxy;

  const isUserConditionMonitoringViewer = (): boolean => {
    const allowedRoles = [
      "view-condition-monitoring",
      "manage-condition-monitoring",
    ];

    return authContext?.permissions?.some((r) => allowedRoles.includes(r));
  };

  const writeSourceInfo = (source: GatewayMachine) => {
    const connectionSource: SourceTypes = {
      ...source,
      source_id: source.opc_ua_data_source_id || "",
    } as SourceTypes;

    dispatch(
      updateConnectionView({
        source: connectionSource,
        state: "Connected",
        authentication: DeviceAuthOption.ANONYMOUS,
      }),
    );

    dispatch(
      updateMaximumNumberOfMonitoredItems(
        source.limits.maximum_number_of_monitored_items,
      ),
    );
  };

  const navigateToNodeManagerSection = (link: NodeManagerActiveViewType) => {
    dispatch(updateNodeManagerActiveView(link));
    goToNodeManager();
  };

  const clickTreeStructureLink = (
    e: MouseEvent | KeyboardEvent,
    source: GatewayMachine,
  ) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("treeStructure");
  };

  const clickDashboardLink = (
    e: MouseEvent | KeyboardEvent,
    source: GatewayMachine,
  ) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("dashboard");
  };

  const clickConditionMonitoringLink = (
    e: MouseEvent | KeyboardEvent,
    source: GatewayMachine,
  ) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("conditionMonitoring");
  };

  const clickMachineSettingsLink = (
    e: MouseEvent | KeyboardEvent,
    source: GatewayMachine,
  ) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("machineSettings");
  };

  const handleOpcUaActionClick = (
    e: MouseEvent | KeyboardEvent,
    action: (
      event: MouseEvent | KeyboardEvent,
      source: GatewayMachine | SourceTypes,
    ) => void,
  ) => {
    e.stopPropagation();
    action(e, sourceForOpcUaActions);
  };

  const clickShotDataTableLink = (
    e: MouseEvent | KeyboardEvent,
    source: GatewayMachine,
  ) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("shotDataTable");
  };

  return {
    texts,
    showOTA,
    showShotDataTable,
    hasOpcUaSkill,
    actionDisabledReason,
    showActionLinks,
    isUserConditionMonitoringViewer,
    handleOpcUaActionClick,
    clickTreeStructureLink,
    clickDashboardLink,
    clickConditionMonitoringLink,
    clickMachineSettingsLink,
    clickShotDataTableLink,
  };
};
